import React from 'react';
import {
  Input,
  Button,
  CheckboxTable,
  Select,
  PaperClip,
  Table,
  Tooltip,
  AreaSelector,
} from 'components';
import { LawyerService, ModeratorService } from 'services';
import { RootState } from 'reducers';
import { RouteComponentProps } from 'react-router-dom';
import { Globals, Constants } from 'utils';
import { UserImage } from 'assets/img';
import { UploadIcon, CheckWhiteIcon, TrashIcon } from 'assets/icons';
import PHONE_CODES from 'utils/phonecodes';
import { isValidPhoneNumber } from 'libphonenumber-js';

/** Formatos aceptados para los documentos del abogado, igual que en
 *  src/screens/lawyer/home/validation.tsx (subida/reemplazo de documentos
 *  por el propio abogado). Se mantiene la misma lista para consistencia. */
const DOCUMENT_FORMATS = [
  'png',
  'pdf',
  'jpg',
  'jpeg',
  'heic',
  'heif',
  'webp',
  'bmp',
  'tiff',
  'tif',
  'avif',
];

interface Props {
  title?: string;
  onClose?: any;
  item?: any;
}

class CreateLawyer extends React.Component<Props> {
  state = {
    form: {
      name: '',
      email: '',
      lastname: '',
      phone: '',
      phone_code: PHONE_CODES[0].value,
      document: '',
      document_type_id: Constants.DOCUMENT_TYPES.V,
      photo: '',
      internal: '',
      area_id: '',
      platform_fee_exempt: false,
      lawyer_document: '',
      // Archivos de documentos del abogado (cédula, inpre, título,
      // especialidad). Se suben/reemplazan igual que en
      // src/screens/lawyer/home/validation.tsx (modo replace), pero desde
      // el panel de admin/moderador.
      identity_document: '' as any,
      inpre: '' as any,
      title: '' as any,
      speciality: '' as any,
    },
    document_types: [],
    user: null,
    user_areas: [],
    areas: [],
    // Flags que indican si el admin presionó X para eliminar un documento
    // opcional existente (título / especialidad). Se envían al backend
    // como remove_title/remove_speciality, igual que en validation.tsx.
    removeTitle: false,
    removeSpeciality: false,
  };

  componentDidMount() {
    this.load();
  }

  load = async () => {
    const res: any = await ModeratorService.admin.getDocumentTypes({
      user_id: this.props.item?.id,
    });
    this.setState(
      {
        document_types: res.document_types || [],
        user: res.user,
        areas: res.areas || [],
      },
      () => {
        if (this.props.item) {
          let reconstructedPhone = this.props.item?.phone || '';
          const savedPhonecodeId = this.props.item?.phonecode_id;

          if (savedPhonecodeId && res.phonecodes?.length > 0) {
            const operadora = res.phonecodes.find((p: any) => p.id === savedPhonecodeId);
            if (operadora) {
              const prefix = operadora.name.replace(/^0/, '');
              if (!reconstructedPhone.startsWith(prefix)) {
                reconstructedPhone = prefix + reconstructedPhone;
              }
            }
          }

          const resolvedPhoneCode = this.props.item?.phone_code
            ? PHONE_CODES.find(
                (p: any) =>
                  p.value === this.props.item.phone_code ||
                  p.callingCode === this.props.item.phone_code,
              )?.value || PHONE_CODES[0].value
            : PHONE_CODES[0].value;

          this.setState({
            form: {
              ...this.state.form,
              name: this.props.item?.name || '',
              email: this.props.item?.email || '',
              lastname: this.props.item?.lastname || '',
              document: this.props.item?.document || '',
              document_type_id: this.props.item?.document_type_id || Constants.DOCUMENT_TYPES.V,
              phone: reconstructedPhone,
              phone_code: resolvedPhoneCode,
              internal: this.props.item?.internal || Constants.LAWYER_INTERNAL.NO,
              platform_fee_exempt: !!this.props.item?.platform_fee_exempt,
              lawyer_document: res.user?.lawyer_document || '',
            },
            user_areas: res.user_areas.map((i: any) => i.area_id) || [],
          });
        }
      },
    );
  };

  submit = async (e: any) => {
    e.preventDefault();

    if (this.state.user_areas.length == 0) {
      Globals.showError('Por favor, ingrese al menos 1 tipo de derecho');
      return;
    }

    if (
      this.state.form.phone &&
      !isValidPhoneNumber(this.state.form.phone, this.state.form.phone_code as any)
    ) {
      Globals.showError('El número de teléfono no es válido para el país seleccionado');
      return;
    }

    // Los campos de documentos (identity_document, inpre, title,
    // speciality) viven en el mismo this.state.form por conveniencia del
    // formulario, pero NO deben enviarse al endpoint /update (datos
    // generales del abogado) — solo al endpoint /update-docs, para no
    // mandar objetos de archivo donde no se esperan.
    const { identity_document, inpre, title, speciality, ...profileForm } = this.state.form;

    try {
      if (this.props.item != null) {
        await LawyerService.admin.update({
          user_id: this.props.item?.id,
          ...profileForm,
          password: '',
          hasFile: true,
          // @ts-ignore,
          photo: this.state.form.photo?.value || '',
          user_areas: JSON.stringify(this.state.user_areas),
        });

        // Si el admin subió algún documento nuevo o marcó eliminar
        // título/especialidad, se guarda además en el endpoint de
        // documentos (admin/lawyers/update-docs), que sube/reemplaza los
        // archivos en storage y reinicia verified a IN_PROCESS salvo que
        // el abogado ya estuviera VERIFIED (en cuyo caso se conserva).
        await this.saveDocumentsIfNeeded();

        Globals.showSuccess('Se ha modificado el abogado correctamente');
        this.props.onClose();
      } else {
        await LawyerService.admin.create({
          ...this.state.form,
          hasFile: true,
          // @ts-ignore,
          photo: this.state.form.photo?.value || '',
          user_areas: JSON.stringify(this.state.user_areas),
        });
        Globals.showSuccess('Se ha creado el abogado correctamente');
        this.props.onClose();
      }
    } catch (err: any) {
      const message = err?.response?.data?.error || err?.friendlyMessage;
      Globals.showError(message || 'Ocurrió un error al guardar el abogado');
    }
  };

  /**
   * saveDocumentsIfNeeded — Envía al backend los documentos nuevos
   * (cédula, inpre, título, especialidad) y/o las eliminaciones
   * solicitadas (remove_title/remove_speciality), solo si hay algo que
   * enviar. Se invoca únicamente en modo edición (this.props.item != null),
   * ya que un abogado recién creado no tiene UserDocument todavía.
   *
   * Reutiliza en backend la misma lógica de home.service.ts::updateDocs
   * (subir y reemplazar, eliminando el archivo anterior en storage).
   */
  saveDocumentsIfNeeded = async () => {
    const { identity_document, inpre, title, speciality, lawyer_document } = this.state.form;
    const { removeTitle, removeSpeciality } = this.state;

    const hasNewFile = !!identity_document || !!inpre || !!title || !!speciality;

    if (!hasNewFile && !removeTitle && !removeSpeciality) {
      // Nada que actualizar en documentos, se evita una llamada innecesaria.
      return;
    }

    const res: any = await LawyerService.admin.updateDocs({
      user_id: this.props.item?.id,
      lawyer_document,
      hasFile: true,
      // withoutError evita que el handler global muestre [object Object]
      withoutError: true,
      // @ts-ignore
      identity_document: identity_document?.value || undefined,
      // @ts-ignore
      inpre: inpre?.value || undefined,
      // @ts-ignore
      title: title?.value || undefined,
      // @ts-ignore
      speciality: speciality?.value || undefined,
      user_areas: JSON.stringify(this.state.user_areas),
      remove_title: removeTitle ? 'true' : undefined,
      remove_speciality: removeSpeciality ? 'true' : undefined,
    });

    if (res?.user) {
      this.setState({ user: res.user });
    }
  };

  change = (e: any, callback = () => {}) => {
    this.setState(
      {
        form: {
          ...this.state.form,
          [e.target.name]: e.target.value,
        },
      },
      callback,
    );
  };

  setInternal = () => {
    const { YES, NO } = Constants.LAWYER_INTERNAL;

    this.setState({
      form: {
        ...this.state.form,
        internal: parseInt(this.state.form.internal) == YES ? NO : YES,
      },
    });
  };

  setPlatformFeeExempt = () => {
    this.setState({
      form: {
        ...this.state.form,
        platform_fee_exempt: !this.state.form.platform_fee_exempt,
      },
    });
  };

  /**
   * confirmRemoveDocument — Pide confirmación y marca un documento
   * opcional (título / especialidad) para ser eliminado al guardar.
   * Misma lógica que en src/screens/lawyer/home/validation.tsx.
   */
  confirmRemoveDocument = (field: 'title' | 'speciality') => {
    Globals.confirm('¿Está seguro que desea eliminar este documento?', () => {
      if (field == 'title') {
        this.setState({ removeTitle: true });
      } else {
        this.setState({ removeSpeciality: true });
      }
    });
  };

  delete = (e: any, index: number) => {
    e.preventDefault();
    let user_areas: any = [...this.state.user_areas];
    user_areas.splice(index, 1);
    this.setState({
      user_areas,
    });
  };

  render() {
    const user: any = this.state.user;

    return (
      <div id='modal-create-lawyer'>
        <form className='row' onSubmit={this.submit}>
          <div className='col-md-12'>
            <div className='container-inputs row'>
              <div className='col-md-6'>
                <Input
                  color='white'
                  value={this.state.form.name}
                  name='name'
                  label='Nombre'
                  onChange={this.change}
                />
              </div>
              <div className='col-md-6'>
                <Input
                  color='white'
                  value={this.state.form.lastname}
                  name='lastname'
                  label='Apellido'
                  onChange={this.change}
                />
              </div>
              <div className='col-md-6'>
                <p className='label'>C.I.</p>
                <div className='container-select'>
                  <Select
                    color='white'
                    placeholder='Seleccionar'
                    name='document_type_id'
                    onChange={this.change}
                    value={this.state.form.document_type_id}
                    options={this.state.document_types.map((item: any) => {
                      return {
                        value: item.id,
                        label: item.name,
                      };
                    })}
                  />
                  <Input
                    type='number'
                    color='white'
                    value={this.state.form.document}
                    name='document'
                    maxLength={9}
                    onChange={this.change}
                  />
                </div>
              </div>
              <div className='col-md-6'>
                <p className='label'>Teléfono</p>
                <div className='container-select'>
                  <Select
                    color='white'
                    placeholder='Código'
                    name='phone_code'
                    style={{ width: 115 }}
                    onChange={this.change}
                    value={this.state.form.phone_code}
                    options={PHONE_CODES}
                  />
                  <Input
                    type='number'
                    color='white'
                    maxLength={15}
                    value={this.state.form.phone}
                    name='phone'
                    onChange={this.change}
                  />
                </div>
              </div>
              <div className='col-md-6'>
                <Input
                  color='white'
                  value={this.state.form.email}
                  name='email'
                  label='Correo'
                  onChange={this.change}
                />
              </div>
              <div className='col-md-6'>
                <div className='check-container' onClick={() => this.setInternal()}>
                  <div
                    className={`check ${parseInt(this.state.form.internal) == Constants.LAWYER_INTERNAL.YES ? 'active' : ''}`}
                  >
                    {parseInt(this.state.form.internal) == Constants.LAWYER_INTERNAL.YES && (
                      <img src={CheckWhiteIcon} />
                    )}
                  </div>
                  <p>Equipo interno</p>
                </div>
              </div>
              <div className='col-md-6'>
                <div className='check-container' onClick={() => this.setPlatformFeeExempt()}>
                  <div className={`check ${this.state.form.platform_fee_exempt ? 'active' : ''}`}>
                    {this.state.form.platform_fee_exempt && <img src={CheckWhiteIcon} />}
                  </div>
                  <p>Sin comisión de plataforma</p>
                </div>
              </div>
              {this.props.item != null && user != null && (
                <div className='col-md-12'>
                  <p className='documents-hint'>
                    Los documentos del abogado se muestran a continuación. La carga de un nuevo
                    archivo sobreescribirá la versión actual.
                  </p>
                </div>
              )}
              {this.props.item != null && user != null && (
                <div className='col-md-6'>
                  <p className='label-document'>Cédula de Identidad:</p>
                  {user?.documents?.identity_document && (
                    <div className='current-document'>
                      <span className='current-document-text'>
                        Documento actual: {user.documents.identity_document.replace('files/', '')}
                      </span>
                    </div>
                  )}
                  <PaperClip
                    formats={DOCUMENT_FORMATS}
                    successText='Foto adjuntada'
                    pendingText='Subir foto o PDF del documento'
                    value={this.state.form.identity_document}
                    className={this.state.form.identity_document ? 'active' : ''}
                    onChange={(e: any) => {
                      this.setState({
                        form: {
                          ...this.state.form,
                          identity_document: e.target,
                        },
                      });
                    }}
                  >
                    <div className='container-upload'>
                      <div className='container-icon'>
                        <img src={UploadIcon} />
                      </div>
                      <p>
                        {this.state.form.identity_document
                          ? 'Foto adjuntada'
                          : 'Subir foto o PDF del documento'}
                      </p>
                    </div>
                  </PaperClip>
                </div>
              )}
              {this.props.item != null && user != null && (
                <div className='col-md-6'>
                  <p className='label-document'>Inpre:</p>
                  {user?.documents?.inpre && (
                    <div className='current-document'>
                      <span className='current-document-text'>
                        Documento actual: {user.documents.inpre.replace('files/', '')}
                      </span>
                    </div>
                  )}
                  <PaperClip
                    formats={DOCUMENT_FORMATS}
                    successText='Foto adjuntada'
                    pendingText='Subir foto o PDF del documento'
                    value={this.state.form.inpre}
                    className={this.state.form.inpre ? 'active' : ''}
                    onChange={(e: any) => {
                      this.setState({
                        form: {
                          ...this.state.form,
                          inpre: e.target,
                        },
                      });
                    }}
                  >
                    <div className='container-upload'>
                      <div className='container-icon'>
                        <img src={UploadIcon} />
                      </div>
                      <p>
                        {this.state.form.inpre
                          ? 'Foto adjuntada'
                          : 'Subir foto o PDF del documento'}
                      </p>
                    </div>
                  </PaperClip>
                </div>
              )}
              {this.props.item != null && user != null && (
                <div className='col-md-6'>
                  <p className='label-document'>Título:</p>
                  {user?.documents?.title && !this.state.removeTitle && (
                    <div className='current-document'>
                      <span className='current-document-text'>
                        Documento actual: {user.documents.title.replace('files/', '')}
                      </span>
                      <Tooltip title='Eliminar archivo actual'>
                        <a
                          className='link-icon link-danger'
                          href='#'
                          onClick={(e: any) => {
                            e.preventDefault();
                            this.confirmRemoveDocument('title');
                          }}
                        >
                          <img src={TrashIcon} alt='Eliminar' />
                        </a>
                      </Tooltip>
                    </div>
                  )}
                  <PaperClip
                    formats={DOCUMENT_FORMATS}
                    successText='Foto adjuntada'
                    pendingText='Subir foto del documento'
                    value={this.state.form.title}
                    className={this.state.form.title ? 'active' : ''}
                    onChange={(e: any) => {
                      this.setState({
                        form: {
                          ...this.state.form,
                          title: e.target,
                        },
                        removeTitle: false,
                      });
                    }}
                  >
                    <div className='container-upload'>
                      <div className='container-icon'>
                        <img src={UploadIcon} />
                      </div>
                      <p>{this.state.form.title ? 'Foto adjuntada' : 'Subir foto del documento'}</p>
                    </div>
                  </PaperClip>
                </div>
              )}
              {this.props.item != null && user != null && (
                <div className='col-md-6'>
                  <p className='label-document'>Especialidad:</p>
                  {user?.documents?.speciality && !this.state.removeSpeciality && (
                    <div className='current-document'>
                      <span className='current-document-text'>
                        Documento actual: {user.documents.speciality.replace('files/', '')}
                      </span>
                      <Tooltip title='Eliminar archivo actual'>
                        <a
                          className='link-icon link-danger'
                          href='#'
                          onClick={(e: any) => {
                            e.preventDefault();
                            this.confirmRemoveDocument('speciality');
                          }}
                        >
                          <img src={TrashIcon} alt='Eliminar' />
                        </a>
                      </Tooltip>
                    </div>
                  )}
                  <PaperClip
                    formats={DOCUMENT_FORMATS}
                    successText='Foto adjuntada'
                    pendingText='Subir foto del documento'
                    value={this.state.form.speciality}
                    className={this.state.form.speciality ? 'active' : ''}
                    onChange={(e: any) => {
                      this.setState({
                        form: {
                          ...this.state.form,
                          speciality: e.target,
                        },
                        removeSpeciality: false,
                      });
                    }}
                  >
                    <div className='container-upload'>
                      <div className='container-icon'>
                        <img src={UploadIcon} />
                      </div>
                      <p>
                        {this.state.form.speciality ? 'Foto adjuntada' : 'Subir foto del documento'}
                      </p>
                    </div>
                  </PaperClip>
                </div>
              )}
            </div>
            <div className='col-md-12'>
              <AreaSelector
                areas={this.state.areas}
                userAreas={this.state.user_areas}
                labelSelect='Agregar tipo de derecho'
                splitLayout={true}
                onChange={(updated) => this.setState({ user_areas: updated })}
              />
            </div>
          </div>
          <div className='col-md-12 text-center'>
            <Button type='submit'>Guardar</Button>
          </div>
        </form>
      </div>
    );
  }
}

export default CreateLawyer;
